import React from 'react';
import { Navigate, Routes, Route } from 'react-router';
import { useTheme } from '@quality24/design-system';
import merge from 'deepmerge';

import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import NavbarTemplate from '@/inpatient-patient-pwa/templates/NavbarTemplate';
import HomePage from '@/inpatient-patient-pwa/pages/HomePage';
import OrderTriggerPage from '@/inpatient-patient-pwa/pages/OrderTriggerPage';
import OrderTrackingPage from '@/inpatient-patient-pwa/pages/OrderTrackingPage';
import PatientCallEvaluationPage from '@/inpatient-patient-pwa/pages/OrderRatingPage';
import DailySurveyPage from '@/inpatient-patient-pwa/pages/DailySurveyPage';
import HospitalInformationPage from '@/inpatient-patient-pwa/pages/HospitalInformationPage';
import PushNotificationProvider from '@/inpatient-patient-pwa/providers/PushNotificationProvider';
import OrderDetailsPage from '../pages/OrderDetailsPage';
import IntegrationLoginPage from '../pages/IntegrationLoginPage';
import { Theme } from '@/inpatient-patient-pwa/types/theme';
import NavbarProvider from '@/inpatient-patient-pwa/contexts/navbar/NavbarProvider';
import PrivateRoute from './PrivateRoute';
import { defaultTheme } from '../styles/theme';

/**
 * <PrivateRouter> component.
 */
const PrivateRouter: React.FunctionComponent = () => {
  const { user } = useAuth<Theme>();
  const { theme, updateTheme } = useTheme();
  const clientTheme = user.hospitalConfig?.theme?.default;

  React.useEffect(() => {
    if (clientTheme) {
      updateTheme(merge(defaultTheme as Partial<Theme>, clientTheme));
    }
  }, [clientTheme, updateTheme]);

  React.useEffect(
    () =>
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', theme.color.primary),
    [theme.color.primary],
  );

  return (
    <PushNotificationProvider>
      <NavbarProvider>
        <Routes>
          {/* Route with greeting (home only) */}
          <Route element={<NavbarTemplate renderGreeting />}>
            {/* Main data route */}
            <Route
              path="/"
              element={
                <PrivateRoute action="home-page:view">
                  <HomePage />
                </PrivateRoute>
              }
            />
          </Route>

          {/* Remaining routes, without greeting */}
          <Route element={<NavbarTemplate />}>
            {/* Hospital Information route - */}
            <Route
              path="information"
              element={
                <PrivateRoute action="home-page:view">
                  <HospitalInformationPage />
                </PrivateRoute>
              }
            />

            {/* Patient call routes */}
            <Route path="calls">
              <Route
                index
                element={
                  <PrivateRoute action="calls-page:view">
                    <OrderTrackingPage />
                  </PrivateRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <PrivateRoute action="calls-page:view">
                    <OrderDetailsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="assistance"
                element={
                  <PrivateRoute action="patient-call-assistance-section:view">
                    <OrderTriggerPage category="ASSISTANCE" />
                  </PrivateRoute>
                }
              />
              <Route
                path="others"
                element={
                  <PrivateRoute action="patient-call-infra-section:view">
                    <OrderTriggerPage category="INFRASTRUCTURE" />
                  </PrivateRoute>
                }
              />
              <Route
                path="group/:serviceGroupId"
                element={
                  <PrivateRoute action="calls-page:view">
                    <OrderTriggerPage />
                  </PrivateRoute>
                }
              />
              {/* Call Evaluation routes */}
              <Route
                path="eval/:evalId"
                element={
                  <PrivateRoute action="patient-call-evaluation:answer">
                    <PatientCallEvaluationPage />
                  </PrivateRoute>
                }
              />
            </Route>

            {/* Survey routes */}
            <Route path="surveys">
              <Route
                path=":surveyId"
                element={
                  <PrivateRoute action="daily-survey-page:view">
                    <DailySurveyPage />
                  </PrivateRoute>
                }
              />
            </Route>

            {/*
             * Logs user out everytime it reaches the /integration endpoint
             * to prevent caching issues.
             */}
            <Route
              path="/integration"
              element={<IntegrationLoginPage authenticatedContext />}
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </NavbarProvider>
    </PushNotificationProvider>
  );
};

export default PrivateRouter;
